/**
 * Tiny in-memory request cache with deduplication and optional
 * stale-while-revalidate persistence to localStorage. No dependency.
 *
 * The problem this solves: on a slow, metered connection, every duplicate
 * request is a real cost. Navbar and Footer both fetch the same
 * 'global' page-content; Navbar and HeroSection both fetch countries.
 * Without this, that's 2x (or 3x) the bytes and round trips for identical
 * data, every single page load.
 *
 * How it works:
 *   - dedupe(key, factory, opts) is the only entry point callers need.
 *     - fresh cached value for `key`?              -> return it, no network call
 *     - identical request already in flight?        -> return that SAME promise
 *     - stale persisted value + persist:true?        -> return it immediately,
 *       and kick off a background refetch (stale-while-revalidate)
 *     - otherwise                                    -> call factory(), cache
 *       the result, return it
 *   - invalidate(pattern) drops matching entries (memory + localStorage) —
 *     call this after any admin mutation that could affect a cached GET.
 *   - clear() drops everything.
 *
 * Persistence rules (never violate these):
 *   - only entries explicitly created with `persist: true` are written to
 *     localStorage. Callers must NEVER pass persist:true for an admin/
 *     authenticated request — this module has no way to know a request is
 *     authenticated, so that responsibility lives entirely with the caller
 *     (see src/services/api.js: only public GETs pass persist:true).
 *   - persisted entries are namespaced under a version key; bumping
 *     CACHE_VERSION invalidates every previously-persisted entry on the
 *     next load, which is the safe thing to do after a shape-changing API
 *     change (e.g. adding pagination).
 *   - persisted entries older than MAX_PERSIST_AGE (24h) are dropped at
 *     hydration time and never served, stale-while-revalidate or otherwise.
 */

const CACHE_VERSION = 'v2'; // v2: drops v1 entries (announcement lists were persisted stale)
const STORAGE_PREFIX = 've_cache:';
const STORAGE_KEY_PREFIX = `${STORAGE_PREFIX}${CACHE_VERSION}:`;
const MAX_PERSIST_AGE = 24 * 60 * 60 * 1000; // 24h

export const TTL = {
  CONTENT: 10 * 60 * 1000, // CMS page-content, countries
  LIST: 2 * 60 * 1000,     // paginated/list endpoints
};

const store = new Map();   // key -> { data, expiresAt }
const inFlight = new Map(); // key -> Promise

function hasLocalStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (_) {
    return false;
  }
}

function persistEntry(key, data) {
  if (!hasLocalStorage()) return;
  try {
    localStorage.setItem(
      STORAGE_KEY_PREFIX + key,
      JSON.stringify({ data, savedAt: Date.now() })
    );
  } catch (_) {
    // Storage full/unavailable (private browsing, quota) — memory cache
    // still works, persistence is a bonus, not a requirement.
  }
}

/** Loads any not-too-old, correctly-versioned entries as stale-but-usable. */
function hydrate() {
  if (!hasLocalStorage()) return;
  const toRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const storageKey = localStorage.key(i);
    if (!storageKey || !storageKey.startsWith(STORAGE_PREFIX)) continue;
    if (!storageKey.startsWith(STORAGE_KEY_PREFIX)) {
      toRemove.push(storageKey); // old cache version — drop it
      continue;
    }
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey));
      if (!raw || Date.now() - raw.savedAt > MAX_PERSIST_AGE) {
        toRemove.push(storageKey);
        continue;
      }
      const key = storageKey.slice(STORAGE_KEY_PREFIX.length);
      // expiresAt in the past -> immediately treated as stale, but present,
      // so dedupe()'s stale-while-revalidate path can use it.
      store.set(key, { data: raw.data, expiresAt: 0 });
    } catch (_) {
      toRemove.push(storageKey);
    }
  }
  toRemove.forEach((k) => localStorage.removeItem(k));
}
hydrate();

function matches(key, pattern) {
  return pattern instanceof RegExp ? pattern.test(key) : key.includes(pattern);
}

/**
 * The main entry point. `factory` is only called when there's no fresh
 * cached value and no identical request already in flight.
 *   ttl      - how long the result is considered fresh (ms)
 *   persist  - also write to localStorage for stale-while-revalidate across
 *              page loads. NEVER pass true for an authenticated request.
 */
export function dedupe(key, factory, { ttl = TTL.LIST, persist = false } = {}) {
  const entry = store.get(key);
  const now = Date.now();

  if (entry && entry.expiresAt > now) {
    return Promise.resolve(entry.data); // fresh — no network call at all
  }

  if (inFlight.has(key)) {
    return inFlight.get(key); // identical request already in flight — share it
  }

  const request = Promise.resolve()
    .then(factory)
    .then((data) => {
      store.set(key, { data, expiresAt: Date.now() + ttl });
      if (persist) persistEntry(key, data);
      return data;
    })
    .finally(() => inFlight.delete(key));

  inFlight.set(key, request);

  // Stale-while-revalidate: we have a persisted-but-expired value, so hand
  // it back immediately and let `request` finish in the background. The
  // NEXT call for this key (after `request` resolves) gets the fresh data.
  // Only persist:true callers opt in to stale-while-revalidate. A stale entry
  // for a persist:false key (e.g. announcements) is never served.
  if (entry && persist) {
    // Nobody awaits `request` on this path — the caller already got
    // `entry.data` and never sees this promise, so a background
    // revalidation failure (backend briefly unreachable, a dropped
    // connection) would otherwise be a genuinely unhandled rejection
    // instead of just... the stale value staying in the cache a bit
    // longer, which is the correct, boring outcome here.
    request.catch(() => {});
    return Promise.resolve(entry.data);
  }

  return request;
}

/** Drop every entry whose key matches (string = substring, or RegExp). */
export function invalidate(pattern) {
  for (const key of Array.from(store.keys())) {
    if (matches(key, pattern)) store.delete(key);
  }
  for (const key of Array.from(inFlight.keys())) {
    if (matches(key, pattern)) inFlight.delete(key);
  }
  if (hasLocalStorage()) {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const storageKey = localStorage.key(i);
      if (!storageKey || !storageKey.startsWith(STORAGE_KEY_PREFIX)) continue;
      if (matches(storageKey.slice(STORAGE_KEY_PREFIX.length), pattern)) {
        localStorage.removeItem(storageKey);
      }
    }
  }
}

/** Drop everything — memory and persisted. */
export function clear() {
  store.clear();
  inFlight.clear();
  if (hasLocalStorage()) {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const storageKey = localStorage.key(i);
      if (storageKey && storageKey.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(storageKey);
      }
    }
  }
}

const cache = { dedupe, invalidate, clear, TTL };
export default cache;
